import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Redirect, useHistory, useParams } from "react-router-dom";
import axios from "axios";
import { FaHome, FaPlus, FaSave, FaTrash } from "react-icons/fa";
import BackButton from "../ChildComponent/BackButton";
import MyLoader from "../Spinner/MyLoader";
import SessionManager from "../Utils/SessionManager";
import myToast from "../Utils/MyToast";
import COLORS from "../resources/colors/COLORS";
import { flattenClasses } from "../dialog/MdlLessonPlanAssign";
import "../css/Admin_css/admin-customize.css";
import "../css/Admin_css/payroll.css";

const API_BASE = () =>
  `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}`;

const HERO_GRADIENT = `linear-gradient(135deg, ${COLORS.primary_color_shade5} 0%, ${COLORS.primary_color_shade7} 55%, ${COLORS.primary_color_shade9} 100%)`;

const STATUS_OPTIONS = ["Draft", "Submitted", "Approved", "Published"];

const EMPTY_FORM = {
  plan_title: "",
  plan_date: "",
  period_no: "",
  duration_mins: 40,
  class_id: "",
  section_id: "",
  subject_id: "",
  staff_id: "",
  chapter_name: "",
  topic_name: "",
  objectives: [""],
  engage_text: "",
  explore_text: "",
  explain_text: "",
  elaborate_text: "",
  evaluate_text: "",
  assessment_text: "",
  resources_text: "",
  approval_status: "Draft",
  assignment_id: "",
};

//Created By Cursor
export default function LessonPlanEditor() {
  const history = useHistory();
  const { plan_id: planIdParam } = useParams();
  const isEdit = Boolean(planIdParam && planIdParam !== "new");
  const loggedIn = localStorage.getItem("token") != null;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sessionId, setSessionId] = useState("");
  const [form, setForm] = useState(EMPTY_FORM);
  const [classData, setClassData] = useState([]);
  const [classSubjects, setClassSubjects] = useState([]);
  const [staffList, setStaffList] = useState([]);

  const classRows = useMemo(() => flattenClasses(classData), [classData]);

  const subjectOptions = useMemo(() => {
    if (!form.class_id || !form.section_id) return [];
    return classSubjects.filter(
      (s) =>
        String(s.class_id) === String(form.class_id) &&
        String(s.section_id) === String(form.section_id)
    );
  }, [classSubjects, form.class_id, form.section_id]);

  useEffect(() => {
    window.scrollTo(0, 0);
    const current = SessionManager.getCurrentSession()?.[0];
    setSessionId(current?.session_id || "");
    const staff = SessionManager.getStaffDetails();
    if (staff?.staff_id && !isEdit) {
      setForm((f) => ({ ...f, staff_id: staff.staff_id }));
    }
    const params = new URLSearchParams(window.location.search);
    const assignmentId = params.get("assignment_id");
    if (assignmentId) {
      setForm((f) => ({ ...f, assignment_id: assignmentId }));
    }
  }, [isEdit]);

  const loadLookups = useCallback(async () => {
    const schoolCode = SessionManager.getSchoolCode();
    if (!schoolCode || !sessionId) return;
    try {
      const [classRes, staffRes] = await Promise.all([
        axios.get(`${API_BASE()}getClass.php`, {
          params: { school_code: schoolCode, session_id: sessionId },
        }),
        axios.get(`${API_BASE()}getStaff.php`, {
          params: { school_code: schoolCode },
        }),
      ]);
      setClassData(
        classRes.data?.api_status === "OK"
          ? classRes.data.data?.class || []
          : []
      );
      setStaffList(
        staffRes.data?.api_status === "OK" && Array.isArray(staffRes.data.staff)
          ? staffRes.data.staff
          : []
      );
    } catch (err) {
      console.error(err);
      myToast.notify_warning("Failed to load lookups.");
    }
  }, [sessionId]);

  const loadSubjects = useCallback(async () => {
    const schoolCode = SessionManager.getSchoolCode();
    if (!schoolCode || !sessionId || !form.class_id || !form.section_id) {
      setClassSubjects([]);
      return;
    }
    try {
      const res = await axios.get(`${API_BASE()}getClassSubject.php`, {
        params: {
          school_code: schoolCode,
          session_id: sessionId,
          class_id: form.class_id,
          section_id: form.section_id,
        },
      });
      setClassSubjects(
        res.data?.api_status === "OK" && Array.isArray(res.data.data)
          ? res.data.data
          : []
      );
    } catch (err) {
      console.error(err);
      setClassSubjects([]);
    }
  }, [sessionId, form.class_id, form.section_id]);

  const loadPlan = useCallback(async () => {
    const schoolCode = SessionManager.getSchoolCode();
    if (!schoolCode || !isEdit) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE()}getLessonPlanById.php`, {
        params: { school_code: schoolCode, plan_id: planIdParam },
      });
      if (res.data?.api_status === "OK" && res.data.data) {
        const p = res.data.data;
        const objs = Array.isArray(p.objectives) && p.objectives.length
          ? p.objectives.map((o) => (typeof o === "string" ? o : o.text || ""))
          : [""];
        setForm({
          plan_title: p.plan_title || "",
          plan_date: p.plan_date || "",
          period_no: p.period_no || "",
          duration_mins: p.duration_mins || 40,
          class_id: p.class_id || "",
          section_id: p.section_id || "",
          subject_id: p.subject_id || "",
          staff_id: p.staff_id || "",
          chapter_name: p.chapter_name || "",
          topic_name: p.topic_name || "",
          objectives: objs,
          engage_text: p.engage_text || "",
          explore_text: p.explore_text || "",
          explain_text: p.explain_text || "",
          elaborate_text: p.elaborate_text || "",
          evaluate_text: p.evaluate_text || "",
          assessment_text: p.assessment_text || "",
          resources_text: p.resources_text || "",
          approval_status: p.approval_status || "Draft",
          assignment_id: "",
        });
      } else {
        myToast.notify_warning("Lesson plan not found.");
        history.push("/AdminMain/LessonPlan");
      }
    } catch (err) {
      console.error(err);
      myToast.notify_warning("Failed to load plan.");
    } finally {
      setLoading(false);
    }
  }, [isEdit, planIdParam, history]);

  useEffect(() => {
    if (sessionId) loadLookups();
  }, [sessionId, loadLookups]);

  useEffect(() => {
    loadSubjects();
  }, [loadSubjects]);

  useEffect(() => {
    if (sessionId) {
      if (isEdit) loadPlan();
      else setLoading(false);
    }
  }, [sessionId, isEdit, loadPlan]);

  const setField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const setObjective = (index, value) => {
    setForm((prev) => {
      const next = [...prev.objectives];
      next[index] = value;
      return { ...prev, objectives: next };
    });
  };

  const addObjective = () => {
    setForm((prev) => ({ ...prev, objectives: [...prev.objectives, ""] }));
  };

  const removeObjective = (index) => {
    setForm((prev) => ({
      ...prev,
      objectives: prev.objectives.filter((_, i) => i !== index),
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const schoolCode = SessionManager.getSchoolCode();
    if (!schoolCode) {
      myToast.notify_warning("School code is missing.");
      return;
    }
    if (!form.plan_title.trim() || !form.class_id || !form.subject_id || !form.staff_id) {
      myToast.notify_warning("Title, class, subject and staff are required.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        action: isEdit ? "update" : "add",
        school_code: schoolCode,
        session_id: sessionId,
        plan_id: isEdit ? planIdParam : undefined,
        ...form,
        objectives: form.objectives.filter((o) => String(o).trim() !== ""),
        plan_title: form.plan_title.trim(),
      };
      const res = await axios.post(`${API_BASE()}saveLessonPlan.php`, payload);
      if (res.data?.api_status === "OK") {
        myToast.notify_success("Lesson plan saved.");
        history.push("/AdminMain/LessonPlan");
      } else {
        myToast.notify_warning(res.data?.api_remark || "Save failed.");
      }
    } catch (err) {
      console.error(err);
      myToast.notify_warning("Save failed.");
    } finally {
      setSaving(false);
    }
  };

  if (!loggedIn) return <Redirect to="/Login" />;
  if (loading) return <MyLoader />;

  return (
    <div className="content payroll-page sm-page lp-page">
      <div
        className="content-header d-flex justify-content-between align-items-center"
        style={{ background: COLORS.primary_color_shade5 }}
      >
        <div className="leftside-content-header d-flex align-items-center gap-2">
          <button
            type="button"
            className="qb-home-btn"
            aria-label="Home"
            onClick={() => history.push("/AdminMain")}
          >
            <FaHome />
          </button>
          <ul className="breadcrumbs mb-0">
            <li>
              <button
                type="button"
                className="am-crumb"
                onClick={() => history.push("/AdminMain/Academic/1")}
              >
                Academic
              </button>
            </li>
            <li>
              <button
                type="button"
                className="am-crumb"
                onClick={() => history.push("/AdminMain/LessonPlan")}
              >
                Lesson Plan
              </button>
            </li>
            <li>
              <span style={{ color: COLORS.white }}>
                {isEdit ? "Edit" : "New"}
              </span>
            </li>
          </ul>
        </div>
        <div className="rightside-content-header">
          <BackButton />
        </div>
      </div>

      <div className="bg_page_white_rounded box_shadow animated fadeInUp sm-shell">
        <header className="sm-hero" style={{ "--sm-hero-bg": HERO_GRADIENT }}>
          <div className="sm-hero__brand">
            <div className="min-w-0">
              <h1 className="sm-hero__title">
                {isEdit ? "Edit lesson plan" : "New lesson plan"}
              </h1>
              <p className="sm-hero__sub">5E model — objectives, method, assessment</p>
            </div>
          </div>
        </header>

        <form className="lp-editor" onSubmit={handleSave}>
          <section className="lp-section">
            <h2>Basic details</h2>
            <div className="row g-3">
              <div className="col-md-8">
                <label className="form-label">Plan title *</label>
                <input
                  className="form-control"
                  value={form.plan_title}
                  onChange={(e) => setField("plan_title", e.target.value)}
                  required
                />
              </div>
              <div className="col-md-4">
                <label className="form-label">Status</label>
                <select
                  className="form-select"
                  value={form.approval_status}
                  onChange={(e) => setField("approval_status", e.target.value)}
                >
                  {STATUS_OPTIONS.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-4">
                <label className="form-label">Class / Section *</label>
                <select
                  className="form-select"
                  value={`${form.class_id}::${form.section_id}`}
                  onChange={(e) => {
                    const [class_id, section_id] = e.target.value.split("::");
                    setField("class_id", class_id);
                    setField("section_id", section_id);
                    setField("subject_id", "");
                  }}
                  required
                >
                  <option value="::">Select</option>
                  {classRows.map((r) => (
                    <option
                      key={`${r.class_id}-${r.section_id}`}
                      value={`${r.class_id}::${r.section_id}`}
                    >
                      {r.class_name} — {r.section_name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-4">
                <label className="form-label">Subject *</label>
                <select
                  className="form-select"
                  value={form.subject_id}
                  onChange={(e) => setField("subject_id", e.target.value)}
                  required
                >
                  <option value="">Select</option>
                  {subjectOptions.map((s) => (
                    <option key={s.subject_id} value={s.subject_id}>
                      {s.subject_name || s.book_name || s.subject_id}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-4">
                <label className="form-label">Teacher *</label>
                <select
                  className="form-select"
                  value={form.staff_id}
                  onChange={(e) => setField("staff_id", e.target.value)}
                  required
                >
                  <option value="">Select</option>
                  {staffList.map((s) => (
                    <option key={s.staff_id} value={s.staff_id}>
                      {s.staff_name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-3">
                <label className="form-label">Date</label>
                <input
                  type="date"
                  className="form-control"
                  value={form.plan_date}
                  onChange={(e) => setField("plan_date", e.target.value)}
                />
              </div>
              <div className="col-md-3">
                <label className="form-label">Period</label>
                <input
                  type="number"
                  min="1"
                  className="form-control"
                  value={form.period_no}
                  onChange={(e) => setField("period_no", e.target.value)}
                />
              </div>
              <div className="col-md-3">
                <label className="form-label">Duration (mins)</label>
                <input
                  type="number"
                  min="15"
                  className="form-control"
                  value={form.duration_mins}
                  onChange={(e) => setField("duration_mins", e.target.value)}
                />
              </div>
              <div className="col-md-3">
                <label className="form-label">Chapter</label>
                <input
                  className="form-control"
                  value={form.chapter_name}
                  onChange={(e) => setField("chapter_name", e.target.value)}
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">Topic</label>
                <input
                  className="form-control"
                  value={form.topic_name}
                  onChange={(e) => setField("topic_name", e.target.value)}
                />
              </div>
            </div>
          </section>

          <section className="lp-section">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h2 className="mb-0">Learning objectives</h2>
              <button type="button" className="btn btn-sm btn-outline-primary" onClick={addObjective}>
                <FaPlus /> Add
              </button>
            </div>
            {form.objectives.map((obj, idx) => (
              <div key={idx} className="input-group mb-2">
                <input
                  className="form-control"
                  value={obj}
                  onChange={(e) => setObjective(idx, e.target.value)}
                  placeholder={`Objective ${idx + 1}`}
                />
                {form.objectives.length > 1 ? (
                  <button
                    type="button"
                    className="btn btn-outline-danger"
                    onClick={() => removeObjective(idx)}
                    aria-label="Remove objective"
                  >
                    <FaTrash />
                  </button>
                ) : null}
              </div>
            ))}
          </section>

          <section className="lp-section">
            <h2>5E teaching method</h2>
            {[
              ["engage_text", "Engage"],
              ["explore_text", "Explore"],
              ["explain_text", "Explain"],
              ["elaborate_text", "Elaborate"],
              ["evaluate_text", "Evaluate"],
            ].map(([key, label]) => (
              <div key={key} className="mb-3">
                <label className="form-label">{label}</label>
                <textarea
                  className="form-control"
                  rows={3}
                  value={form[key]}
                  onChange={(e) => setField(key, e.target.value)}
                />
              </div>
            ))}
          </section>

          <section className="lp-section">
            <h2>Assessment & resources</h2>
            <div className="mb-3">
              <label className="form-label">Assessment</label>
              <textarea
                className="form-control"
                rows={3}
                value={form.assessment_text}
                onChange={(e) => setField("assessment_text", e.target.value)}
              />
            </div>
            <div>
              <label className="form-label">Resources</label>
              <textarea
                className="form-control"
                rows={3}
                value={form.resources_text}
                onChange={(e) => setField("resources_text", e.target.value)}
              />
            </div>
          </section>

          <div className="lp-actions">
            <button
              type="button"
              className="btn btn-light"
              onClick={() => history.push("/AdminMain/LessonPlan")}
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              <FaSave /> {saving ? "Saving…" : "Save plan"}
            </button>
          </div>
        </form>
      </div>
      {saving ? <MyLoader /> : null}

      <style>{`
        .lp-editor { padding: 1.25rem 1.5rem 2rem; }
        .lp-section { margin-bottom: 1.75rem; }
        .lp-section h2 { font-size: 1.05rem; font-weight: 700; color: #0f172a; margin-bottom: 0.85rem; }
        .lp-actions { display: flex; gap: 0.75rem; justify-content: flex-end; padding-top: 0.5rem; border-top: 1px solid #e8ecf4; }
      `}</style>
    </div>
  );
}
